import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Clock, MessageSquareText } from "lucide-react";
import { IMAGES, PHONE_DISPLAY, QUOTE_SMS_BODY, smsLink } from "@/data/site";
import { useSectionNav } from "@/hooks/useSectionNav";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const go = useSectionNav();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Detailer hand washing a car wheel with foam during a mobile auto detail in Gap, PA"
          className="hero-img-drift h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/75 to-obsidian/35" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 via-transparent to-transparent" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8 sm:pb-28">
        <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-white" data-testid="hero-headline">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block text-4xl sm:text-6xl lg:text-7xl xl:text-8xl">
              Professional Mobile Detailing —
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block text-4xl sm:text-6xl lg:text-7xl xl:text-8xl">
              We Come <span className="text-blue-500">To You</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-5 font-mono text-xs font-semibold uppercase tracking-[0.28em] text-slate-300 sm:text-sm"
          data-testid="hero-area-line"
        >
          Serving Chester &amp; Lancaster Counties
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-8 flex items-start gap-3 font-display text-lg font-bold uppercase tracking-wide text-white sm:items-center sm:text-2xl lg:text-3xl"
          data-testid="hero-response-line"
        >
          <Clock size={24} strokeWidth={2.2} className="mt-0.5 shrink-0 text-blue-500 sm:mt-0" aria-hidden="true" />
          <span>
            Request a Free Quote — <span className="text-blue-400">Response Within 24 Hours</span>
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <button onClick={() => go("quote")} className="btn-primary" data-testid="hero-quote-button">
            Get a Free Quote
            <ArrowRight size={17} strokeWidth={2.4} />
          </button>
          <a href={smsLink(QUOTE_SMS_BODY)} className="btn-outline" data-testid="hero-text-button">
            <MessageSquareText size={17} strokeWidth={2.2} />
            Text {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400"
          data-testid="hero-vehicle-line"
        >
          Cars <span className="text-blue-500">•</span> SUVs <span className="text-blue-500">•</span> Trucks{" "}
          <span className="text-blue-500">•</span> Vans <span className="text-blue-500">•</span> Work Vehicles
        </motion.p>
      </motion.div>
    </section>
  );
};

export default Hero;
